<template>
  <v-card class="seccion-card service-usage">
    <v-card-title class="section-head usage-head">
      <span><v-icon small>mdi-chart-box-outline</v-icon>Uso de servicios</span>
      <v-btn-toggle
        v-if="showUnitToggle"
        v-model="unit"
        mandatory
        dense
        color="primary"
        data-test="unit-toggle"
      >
        <v-btn small value="CLP" data-test="unit-clp">CLP</v-btn>
        <v-btn small value="UF" data-test="unit-uf">UF</v-btn>
      </v-btn-toggle>
    </v-card-title>

    <div v-if="!membershipId" class="empty-state" data-test="no-memberships">
      El usuario no tiene membresías
    </div>

    <template v-else>
      <v-skeleton-loader v-if="loading" type="table" class="px-4" data-test="loading" />

      <v-alert v-else-if="error" type="error" text dense class="ma-4" data-test="error">
        No se pudo cargar el uso de servicios.
        <template v-slot:append>
          <v-btn small text color="error" data-test="retry" @click="fetchUsage">Reintentar</v-btn>
        </template>
      </v-alert>

      <template v-else-if="usage">
        <v-alert
          v-if="!usage.membership.isActive"
          type="info"
          text
          dense
          class="mx-4 mt-3 mb-0"
          data-test="inactive-alert"
        >
          Membresía no vigente: se muestran usos y topes históricos; no se evalúan carencia ni edad.
        </v-alert>

        <div class="kpis" data-test="kpis">
          <div v-for="kpi in kpis" :key="kpi.key" class="kpi" :data-test="`kpi-${kpi.key}`">
            <span class="kpi-label">{{ kpi.label }}</span>
            <span class="kpi-value">{{ kpi.value }}</span>
          </div>
        </div>

        <div v-if="!usage.services.length" class="empty-state" data-test="no-services">
          El plan no tiene servicios configurados
        </div>

        <template v-else>
          <!-- Un plan tiene pocos servicios: la tabla va completa, sin filtros ni paginación. -->
          <div class="table-scroll">
            <v-data-table
              :headers="headers"
              :items="usage.services"
              item-key="servicePlanId"
              disable-pagination
              hide-default-footer
              class="usage-table pdf-friendly-table"
            >
              <template v-slot:item.coverageMode="{ item }">
                {{ coverageModeLabel(item.coverageMode) }}
              </template>
              <template v-slot:item.events="{ item }">
                <div class="usage-cell">
                  <span>{{ item.events.used }} / {{ item.events.total === null ? '∞' : item.events.total }}</span>
                  <v-progress-linear
                    v-if="usagePercent(item.events) !== null"
                    :value="usagePercent(item.events)"
                    :color="barColor(item.events)"
                    height="6"
                    rounded
                  />
                  <span class="usage-sub">{{ eventsAvailableText(item) }}</span>
                </div>
              </template>
              <template v-slot:item.amount="{ item }">
                <div class="usage-cell">
                  <span>{{ amount(item.amount.used, item) }} / {{ item.amount.total === null ? '—' : amount(item.amount.total, item) }}</span>
                  <v-progress-linear
                    v-if="usagePercent(item.amount) !== null"
                    :value="usagePercent(item.amount)"
                    :color="barColor(item.amount)"
                    height="6"
                    rounded
                  />
                  <span v-if="item.amount.available !== null" class="usage-sub">
                    Disponible {{ amount(item.amount.available, item) }}
                  </span>
                </div>
              </template>
              <template v-slot:item.maxAmountPerEvent="{ item }">
                {{ perEvent(item) }}
              </template>
              <template v-slot:item.deductible="{ item }">
                {{ formatDeductible(item.deductible) }}
              </template>
              <template v-slot:item.lastUsedAt="{ item }">
                {{ formatIsoDate(item.lastUsedAt) }}
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip small outlined :color="statusChip(item).color" class="font-weight-bold">
                  {{ statusChip(item).label }}
                </v-chip>
              </template>
            </v-data-table>
          </div>
        </template>

        <div class="usage-footer" data-test="footer">{{ footerText }}</div>
      </template>
    </template>
  </v-card>
</template>

<script>
import Api360Service from '@/services/360.service.js';
import {
  toDisplayAmount,
  formatPerEvent,
  coverageModeLabel,
  statusChip,
  formatDeductible,
  formatIsoDate,
  formatUfValue,
  usagePercent
} from '@/utils/service-usage.utils';

export default {
  name: 'MembershipServiceUsage',
  props: {
    // Membresía a mostrar; la elige la lista de la pestaña «Coberturas y servicios».
    membershipId: { type: String, default: null },
    token: { type: String, required: true }
  },
  data() {
    return {
      usage: null,
      loading: false,
      error: false,
      // Parte en UF para que los servicios con cobertura en UF se vean en su propia moneda.
      unit: 'UF',
      headers: [
        { text: 'Servicio', value: 'title', sortable: false },
        { text: 'Modalidad', value: 'coverageMode', sortable: false },
        { text: 'Usos', value: 'events', sortable: false },
        { text: 'Monto cubierto', value: 'amount', sortable: false },
        { text: 'Tope por uso', value: 'maxAmountPerEvent', sortable: false },
        { text: 'Deducible', value: 'deductible', sortable: false },
        { text: 'Último uso', value: 'lastUsedAt', sortable: false },
        { text: 'Estado', value: 'status', sortable: false }
      ],
      // Evita que una respuesta lenta de una membresía anterior pise la actual.
      requestId: 0
    };
  },
  computed: {
    // Solo tiene sentido convertir si algún servicio del plan tiene su cobertura en UF.
    showUnitToggle() {
      return Boolean(
        this.usage &&
          this.usage.membership.currency === 'CLP' &&
          this.usage.uf &&
          this.usage.services.some((s) => s.ufCoverage)
      );
    },
    amountContext() {
      return {
        currency: this.usage ? this.usage.membership.currency : 'CLP',
        unit: this.showUnitToggle ? this.unit : null,
        ufValue: this.usage && this.usage.uf ? this.usage.uf.value : null
      };
    },
    kpis() {
      const { summary } = this.usage;
      return [
        // Suma servicios en pesos y en UF: se mantiene en la moneda común de los servicios.
        { key: 'amount', label: 'Monto cubierto (vigencia)', value: this.amount(summary.totalUsedAmount, null) },
        { key: 'events', label: 'Usos (vigencia)', value: summary.totalUsedEvents },
        { key: 'exhausted', label: 'Servicios agotados', value: `${summary.exhaustedCount} de ${summary.servicesCount}` },
        { key: 'grace', label: 'En carencia', value: summary.inGracePeriodCount },
        { key: 'renewal', label: 'Topes se renuevan', value: 'Con nueva membresía' }
      ];
    },
    footerText() {
      const { membership, uf } = this.usage;
      const currencyText =
        this.amountContext.unit === 'UF'
          ? `Montos en ${membership.currency}; servicios con cobertura en UF expresados en UF`
          : `Montos en ${membership.currency}`;
      const ufText = uf ? `UF ${formatUfValue(uf.value)} (${formatIsoDate(uf.date)})` : 'Valor UF no disponible';
      return `${currencyText} · ${ufText} · Incluye usos en periodo de carencia`;
    }
  },
  watch: {
    membershipId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.fetchUsage();
        } else {
          this.requestId++;
          this.usage = null;
        }
      }
    }
  },
  methods: {
    async fetchUsage() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = false;
      try {
        const usage = await Api360Service.getMembershipServiceUsage(this.membershipId, this.token);
        if (requestId !== this.requestId) return;
        this.usage = usage;
      } catch (error) {
        // La falla se muestra dentro de la sección (con reintentar) sin bloquear el resto del 360.
        if (requestId !== this.requestId) return;
        this.usage = null;
        this.error = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    // Cada servicio se muestra en su propia moneda; el toggle UF solo afecta a los servicios con cobertura en UF.
    contextFor(item) {
      const currency = (item && item.currency) || this.amountContext.currency;
      const unit = item && item.ufCoverage ? this.amountContext.unit : null;
      return { ...this.amountContext, currency, unit };
    },
    amount(value, item) {
      return toDisplayAmount(value, this.contextFor(item));
    },
    perEvent(item) {
      return formatPerEvent(item.maxAmountPerEvent, this.contextFor(item));
    },
    barColor(usage) {
      const percent = usagePercent(usage);
      if (percent >= 100) return 'red';
      if (percent >= 80) return 'orange';
      return 'green';
    },
    eventsAvailableText(item) {
      const { available } = item.events;
      if (available === null) return 'Sin tope';
      return available === 1 ? '1 disponible' : `${available} disponibles`;
    },
    usagePercent,
    coverageModeLabel,
    statusChip,
    formatDeductible,
    formatIsoDate
  }
};
</script>

<style scoped>
.seccion-card {
  margin-bottom: 20px;
}

.section-head {
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.section-head .v-icon {
  margin-right: 8px;
  color: var(--pw-text-muted);
}

.usage-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  padding: 16px 20px;
}
.kpi {
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border: 1px solid var(--pw-divider);
  border-radius: 8px;
}
.kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-faint);
}
.kpi-value {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Comparte el ancho con la lista de membresías: el disponible va bajo cada uso para no exigir
   scroll lateral; solo en pantallas angostas la tabla se desplaza. */
.table-scroll {
  overflow-x: auto;
}
.usage-table {
  min-width: 680px;
}
.usage-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  padding: 6px 0;
  font-variant-numeric: tabular-nums;
}

/* Mismo formato que las tablas de Vision360Table: el marco lo pone la tarjeta. */
.pdf-friendly-table {
  border: 0;
}

.usage-sub {
  font-size: 0.75rem;
  color: var(--pw-text-muted);
}

.usage-footer {
  padding: 10px 20px 14px;
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}

.empty-state {
  padding: 24px 20px;
  color: var(--pw-text-muted);
}
</style>
